import React from 'react'
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { INTEREST_LABELS, SOURCE_OPTIONS, TIER_NAMES } from '../kit/helpers'

// Form field groups shared by the new-lead dialog and the inline editors on
// the lead page. `form` is the draft object, `setField(name, value)` updates it.

export const ContactFields = ({ form, setField, showOrganisation = true, autoFocus }) => (
  <Grid container spacing={1.5}>
    {showOrganisation && (
      <Grid item xs={12}>
        <TextField
          label="Organisation"
          value={form.organisationName ?? ''}
          onChange={(event) => setField('organisationName', event.target.value)}
          required
          fullWidth
          size="small"
          autoFocus={autoFocus}
        />
      </Grid>
    )}
    <Grid item xs={12} md={6}>
      <TextField
        label="Contact name"
        value={form.contactName ?? ''}
        onChange={(event) => setField('contactName', event.target.value)}
        fullWidth
        size="small"
        autoFocus={autoFocus && !showOrganisation}
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Email"
        type="email"
        value={form.contactEmail ?? ''}
        onChange={(event) => setField('contactEmail', event.target.value)}
        fullWidth
        size="small"
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <TextField
        label="Phone"
        value={form.contactPhone ?? ''}
        onChange={(event) => setField('contactPhone', event.target.value)}
        fullWidth
        size="small"
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <Autocomplete
        freeSolo
        options={SOURCE_OPTIONS}
        value={form.source ?? ''}
        inputValue={form.source ?? ''}
        onChange={(event, value) => setField('source', value ?? '')}
        onInputChange={(event, value) => setField('source', value ?? '')}
        renderInput={(params) => <TextField {...params} label="Source" size="small" />}
      />
    </Grid>
    <Grid item xs={12}>
      <FormControlLabel
        control={
          <Switch
            checked={!!form.mailingList}
            onChange={(event) => setField('mailingList', event.target.checked)}
          />
        }
        label="On the mailing list"
      />
    </Grid>
  </Grid>
)

export const InterestField = ({ value, onChange, label = 'Interest' }) => (
  <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Rating
      value={Number(value) || 0}
      onChange={(event, next) => onChange(next)}
      max={5}
    />
    <Typography variant="body2" color="text.secondary">
      {INTEREST_LABELS[Number(value)] ?? 'Not rated'}
    </Typography>
  </Stack>
)

export const ProfileFields = ({ form, setField }) => (
  <Grid container spacing={1.5}>
    <Grid item xs={12} md={6}>
      <TextField
        label="Staff numbers"
        value={form.staffNumbers ?? ''}
        onChange={(event) => setField('staffNumbers', event.target.value)}
        fullWidth
        size="small"
        placeholder="e.g. 40 incl. bank"
      />
    </Grid>
    <Grid item xs={12} md={6}>
      <FormControl fullWidth size="small">
        <InputLabel>Likely plan</InputLabel>
        <Select
          value={form.likelyTier ?? ''}
          label="Likely plan"
          onChange={(event) => setField('likelyTier', event.target.value)}
        >
          <MenuItem value="">Not sure</MenuItem>
          {Object.entries(TIER_NAMES).map(([tier, name]) => (
            <MenuItem key={tier} value={Number(tier)}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
    <Grid item xs={12}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        SERVICES
      </Typography>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!form.providesEventServices}
              onChange={(event) => setField('providesEventServices', event.target.checked)}
            />
          }
          label="Event medical"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!form.providesPTS}
              onChange={(event) => setField('providesPTS', event.target.checked)}
            />
          }
          label="Patient transport"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!form.providesCommercialTraining}
              onChange={(event) => setField('providesCommercialTraining', event.target.checked)}
            />
          }
          label="Commercial training"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!form.isCQCregistered}
              onChange={(event) => setField('isCQCregistered', event.target.checked)}
            />
          }
          label="CQC registered"
        />
      </FormGroup>
    </Grid>
    <Grid item xs={12}>
      <TextField
        label="Existing challenges"
        value={form.existingChallenges ?? ''}
        onChange={(event) => setField('existingChallenges', event.target.value)}
        fullWidth
        size="small"
        multiline
        minRows={2}
        placeholder="What are they struggling with today?"
      />
    </Grid>
  </Grid>
)
